/**
 * The Courses tab's pure helpers and its one catalog read (plan
 * docs/plans/lms-plan.md section 13 tab 2, 7.2, 8.3, 8.4, 18.17, 18.22;
 * WP-42; R3, R9, R24).
 *
 * - loadCatalog walks GET /courses page by page (Appendix A caps a page at
 *   200; the catalog is small, but a silent cut-off would hide a course, G3).
 * - isListable is the learner's view of 18.17: an ACTIVE course with a
 *   published current version. The route already answers only those; the
 *   screen re-checks so a retired course (is_active false) can never be listed.
 * - statusChip turns one GET /me/status cell (8.4) into the chip section 13
 *   names: "compliant until {date}, due {date}, overdue, not started", each
 *   state with its word and the 13 colour (compliant green, expiring amber,
 *   assigned primary, overdue red, expired red, waived gray, missing amber,
 *   not_required gray). A course absent from the map is "Not started".
 * - courseAction decides the course page's one button: "Review" for a course
 *   the person is current on with no open assignment (the player opens at its
 *   Overview and starts no attempt; the quiz answers LMS_ALREADY_CURRENT and
 *   the player offers "Take the quiz anyway", 18.22), "Continue" once the
 *   bookmark shows progress (the player's own hasStarted rule), else "Start".
 */
import { format } from 'date-fns';
import { listCourses } from '../../api/courses';
import type { CourseListItem } from '../../api/courses';
import type { MyCourseStatus } from '../../api/completions';
import type { LmsProgress } from '../../api/progress';
import type { CourseKind } from '../../content/types';
import type { ComplianceStatus } from '../../lib/complianceStatus';

// ---------------------------------------------------------------------------
// Categories (5.2 lms_courses.category; section 13 lists them in this order)
// ---------------------------------------------------------------------------

/** Section 13 tab 2: compliance, clinical skills, best practices, SOPs, custom (platform, 5.2, before custom). */
export const CATEGORY_ORDER: readonly string[] = ['compliance', 'clinical_skills', 'best_practices', 'sop', 'platform', 'custom'];

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  compliance: 'Compliance',
  clinical_skills: 'Clinical skills',
  best_practices: 'Best practices',
  sop: 'SOPs',
  platform: 'Platform',
  custom: 'Custom',
};

/** The heading a category is shown under; an unknown value is shown as it came (never hidden, G3). */
export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/** How a course is taken (5.2 lms_courses.kind; 7.2, 7.4, 11.3). */
export function kindLabel(kind: CourseKind | string): string {
  switch (kind) {
    case 'read_ack':
      return 'Read and acknowledge';
    case 'competency':
      return 'Quiz and an observed sign-off';
    case 'quiz':
    default:
      return 'Slides, reading and a quiz';
  }
}

// ---------------------------------------------------------------------------
// The catalog read
// ---------------------------------------------------------------------------

/** Appendix A: a list page holds at most 200 items. */
export const CATALOG_PAGE_SIZE = 200;

/** 50 pages of 200: far beyond any network's catalog; reaching it is an error, not a silent cut (G3). */
export const CATALOG_MAX_PAGES = 50;

/** Every course GET /courses answers, all pages. */
export async function loadCatalog(): Promise<CourseListItem[]> {
  const out: CourseListItem[] = [];
  for (let page = 0; page < CATALOG_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await listCourses({ limit: CATALOG_PAGE_SIZE, offset: page * CATALOG_PAGE_SIZE });
    const items = Array.isArray(res?.items) ? res.items : [];
    out.push(...items);
    if (!res?.page?.hasMore || items.length === 0) return out;
  }
  throw new Error('The catalog holds more courses than this screen can list.');
}

/** 18.17: a learner is offered active courses with a published current version, never a retired one. */
export function isListable(course: CourseListItem): boolean {
  return course.is_active !== false && course.current_version !== null && course.current_version !== undefined;
}

/** Every word of the search appears in the title or the slug (case-insensitive). */
export function matchesSearch(course: CourseListItem, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = `${course.title} ${course.slug}`.toLowerCase();
  return words.every((w) => haystack.includes(w));
}

export interface CourseGroup {
  category: string;
  label: string;
  courses: CourseListItem[];
}

/** The courses grouped by category in section 13's order, unknown categories last; each group keeps the server's title order. */
export function groupByCategory(courses: CourseListItem[]): CourseGroup[] {
  const byCategory = new Map<string, CourseListItem[]>();
  for (const course of courses) {
    const key = String(course.category);
    const list = byCategory.get(key);
    if (list) list.push(course);
    else byCategory.set(key, [course]);
  }
  const known = CATEGORY_ORDER.filter((c) => byCategory.has(c));
  const unknown = Array.from(byCategory.keys())
    .filter((c) => !CATEGORY_ORDER.includes(c))
    .sort();
  return [...known, ...unknown].map((category) => ({
    category,
    label: categoryLabel(category),
    courses: byCategory.get(category) ?? [],
  }));
}

// ---------------------------------------------------------------------------
// Words for numbers and dates
// ---------------------------------------------------------------------------

/** An instant (due_at, expires_at, published_at) as a local day, or null when absent or invalid. */
export function formatInstant(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : format(d, 'd MMM yyyy');
}

/**
 * A calendar date (YYYY-MM-DD: a source's accessed_on, an SOP's effective_on)
 * as that same day. Parsed as a local date on purpose: new Date('2026-09-12')
 * is UTC midnight and would read as 11 Sep west of Greenwich.
 */
export function formatDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return formatInstant(value);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : format(d, 'd MMM yyyy');
}

/** 5.2 validity_months: NULL means the course does not expire. */
export function validityText(months: number | null | undefined): string {
  if (typeof months !== 'number' || !Number.isFinite(months) || months <= 0) return 'Does not expire';
  return months === 1 ? 'Valid for 1 month' : `Valid for ${months} months`;
}

/** 8.3: the daily job assigns the renewal once the expiry is within renewal_lead_days. */
export function renewalText(months: number | null | undefined, leadDays: number | null | undefined): string | null {
  if (typeof months !== 'number' || months <= 0) return null;
  if (typeof leadDays !== 'number' || !Number.isFinite(leadDays) || leadDays <= 0) return 'A renewal is assigned when it expires.';
  return leadDays === 1 ? 'A renewal is assigned 1 day before it expires.' : `A renewal is assigned ${leadDays} days before it expires.`;
}

/** The list's short form of estimated_minutes. */
export function minutesShort(minutes: number | null | undefined): string | null {
  return typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0 ? `${minutes} min` : null;
}

/** The course page's form of estimated_minutes (the player's wording). */
export function minutesLong(minutes: number | null | undefined): string | null {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) return null;
  return minutes === 1 ? 'About 1 minute' : `About ${minutes} minutes`;
}

// ---------------------------------------------------------------------------
// The status chip (8.4, section 13)
// ---------------------------------------------------------------------------

export type ChipTone = 'green' | 'amber' | 'red' | 'gray' | 'primary';

export interface StatusChipModel {
  /** The 8.4 status, or 'not_started' for a course absent from GET /me/status. */
  status: ComplianceStatus | 'not_started';
  tone: ChipTone;
  /** The word, with its date where section 13 gives one. */
  text: string;
}

/** One GET /me/status cell (absent: the person has nothing on the course) as its chip. */
export function statusChip(cell: MyCourseStatus | null | undefined): StatusChipModel {
  if (!cell) return { status: 'not_started', tone: 'gray', text: 'Not started' };
  const expires = formatInstant(cell.expires_at);
  const due = formatInstant(cell.due_at);
  switch (cell.status) {
    case 'compliant':
      return { status: 'compliant', tone: 'green', text: expires ? `Compliant until ${expires}` : 'Compliant' };
    case 'expiring':
      return { status: 'expiring', tone: 'amber', text: expires ? `Expiring ${expires}` : 'Expiring' };
    case 'assigned':
      return { status: 'assigned', tone: 'primary', text: due ? `Due ${due}` : 'Assigned' };
    case 'overdue':
      return { status: 'overdue', tone: 'red', text: 'Overdue' };
    case 'expired':
      return { status: 'expired', tone: 'red', text: expires ? `Expired ${expires}` : 'Expired' };
    case 'waived':
      return { status: 'waived', tone: 'gray', text: 'Waived' };
    case 'missing':
      // 8.4 "missing": a profile requires the course and nothing is assigned yet.
      return { status: 'missing', tone: 'amber', text: 'Required' };
    case 'not_required':
    default:
      return { status: cell.status === 'not_required' ? 'not_required' : 'not_started', tone: 'gray', text: 'Not started' };
  }
}

// ---------------------------------------------------------------------------
// The course page's button (7.2, 18.22)
// ---------------------------------------------------------------------------

export type CourseActionKind = 'start' | 'continue' | 'review';

export interface CourseAction {
  kind: CourseActionKind;
  label: string;
}

/**
 * Current on the course (a valid completion, 8.4 compliant or expiring) with
 * no open assignment: "Review". An open renewal carries assignment_id, and the
 * server takes a quiz against an open assignment without force (WP-34), so
 * that person gets Start or Continue instead.
 */
export function isCurrentWithoutAssignment(cell: MyCourseStatus | null | undefined): boolean {
  return Boolean(cell && (cell.status === 'compliant' || cell.status === 'expiring') && !cell.assignment_id);
}

/** The player's hasStarted rule (hooks/usePlayer.ts), plus a reading already marked done. */
export function hasProgress(progress: LmsProgress | null | undefined): boolean {
  if (!progress) return false;
  return Boolean(progress.last_slide_id || (Array.isArray(progress.slides_seen) && progress.slides_seen.length > 0) || progress.reading_done);
}

export function courseAction(cell: MyCourseStatus | null | undefined, progress: LmsProgress | null | undefined): CourseAction {
  if (isCurrentWithoutAssignment(cell)) return { kind: 'review', label: 'Review' };
  if (hasProgress(progress)) return { kind: 'continue', label: 'Continue' };
  return { kind: 'start', label: 'Start' };
}

/** The player route (18.3). */
export function learnHref(courseId: string): string {
  return `/lms/courses/${encodeURIComponent(courseId)}/learn`;
}

/** The course page route (18.3). */
export function courseHref(courseId: string): string {
  return `/lms/courses/${encodeURIComponent(courseId)}`;
}
